import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppLayout from "../layouts/AppLayout.jsx";
import NavSocialMedia from "../components/NavSocialMedia.jsx";
import Filters from "../components/Filters.jsx";
import CaptionCard from "../components/CaptionCard.jsx";
import API from "../config/api.js";

export default function Captions() {
  const location = useLocation();
  const navigate = useNavigate();

  const [captions, setCaptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategories, setActiveCategories] = useState([]);
  const [isNew, setIsNew] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [buttonDisabled, setButtonDisabled] = useState(true);

  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API.categories}?list`, {
        credentials: "include",
      });

      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error", err);
      setCategories([]);
    }
  }, []);

  const fetchCaptions = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams(location.search);
      const search = params.get("search") || "";

      const formData = new FormData();
      formData.append("currentPage", page);

      if (isNew) {
        formData.append("new", "true");
      }

      if (search.trim()) {
        formData.append("search", search.trim());
      }

      activeCategories.forEach((id) => {
        formData.append("idcategory[]", id);
      });

      const res = await fetch(`${API.captions}?list&randomByIduserAndDay`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await res.json();

      if (!Array.isArray(data) || data.length === 0) {
        if (page === 1) {
          setCaptions([]);
        }
        setHasMore(false);
        return;
      }

      if (page === 1) {
        setCaptions(data);
      } else {
        setCaptions((prev) => [...prev, ...data]);
      }
    } catch (err) {
      console.error("Error", err);

      if (page === 1) {
        setCaptions([]);
      }

      setHasMore(false);
    } finally {
      setLoading(false);
      setButtonDisabled(true);
      setTimeout(() => setButtonDisabled(false), 3000);
    }
  }, [location.search, page, isNew, activeCategories]);

  const updateUrl = (selectedCategories, newFlag) => {
    const currentParams = new URLSearchParams(location.search);
    const params = new URLSearchParams();

    const search = currentParams.get("search");
    if (search) {
      params.set("search", search);
    }

    (selectedCategories || []).forEach((id) => {
      params.append("idcategory", id);
    });

    if (newFlag) {
      params.set("new", "true");
    }

    const query = params.toString();
    navigate(query ? `/captions?${query}` : "/captions");
  };

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ids = params.getAll("idcategory");

    const newFlag =
      params.has("new") &&
      (params.get("new") === "" ||
        params.get("new") === "true" ||
        params.get("new") === "1");

    setCaptions([]);
    setPage(1);
    setHasMore(true);
    setActiveCategories(ids);
    setIsNew(newFlag);
  }, [location.search]);

  useEffect(() => {
    fetchCaptions();
  }, [fetchCaptions]);

  // Infinite scroll com IntersectionObserver
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!hasMore || loading || buttonDisabled) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, buttonDisabled]);

  const handleApplyFilters = (selectedCategories) => {
    updateUrl(selectedCategories || [], isNew);
  };

  const handleToggleNew = () => {
    updateUrl(activeCategories, !isNew);
  };

  const handleLoadMore = () => {
    if (!buttonDisabled && !loading && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  const placeholders = Array.from({ length: 6 }, (_, i) => (
    <div key={`placeholder-${i}`} className="col-md-4 placeholder-item">
      <div className="card rounded-4 h-100 border caption animate__animated animate__fadeIn animate__slow">
        <div className="card-header bg-white rounded-4 border-0 p-3 pb-0">
          <div className="mb-2 categories">
            <span className="placeholder col-6"></span>
          </div>
          <h5 className="card-title fs-5 mb-0">
            <span className="placeholder col-8"></span>
          </h5>
        </div>
        <div className="card-body">
          <p className="card-text caption-content">
            <span className="placeholder col-7"></span><br />
            <span className="placeholder col-4"></span><br />
            <span className="placeholder col-6"></span><br />
            <span className="placeholder col-8"></span><br />
          </p>
        </div>
        <div className="card-footer rounded-bottom-4 bg-transparent p-3">
          <div className="row align-items-center">
            <div className="col-auto">
              <button type="button" className="btn btn-outline-primary disabled placeholder" disabled>
                Loading...
              </button>
            </div>
            <div className="col text-end action-buttons">
              <span className="placeholder col-1 me-2"></span>
              <span className="placeholder col-1 me-2"></span>
              <span className="placeholder col-1"></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  ));

  return (
    <AppLayout currentPage="captions">
      <NavSocialMedia currentPage="captions" />

      <Filters
        categories={categories}
        activeCategories={activeCategories}
        onApplyFilters={handleApplyFilters}
        isNew={isNew}
        onToggleNew={handleToggleNew}
        filterTypes={[
          { type: "caption", label: "Content Pillar" },
          { type: "doneforyou", label: "Niche" },
          { type: "destination", label: "Destination" },
        ]}
      />

      <div className="row g-2">
        {loading && captions.length === 0
          ? placeholders
          : captions.map((caption) => (
              <div key={caption.idcaption} className="col-md-4">
                <CaptionCard caption={caption} />
              </div>
            ))}
      </div>

      {hasMore && captions.length > 0 && (
        <div className="text-center mt-4" ref={sentinelRef}>
          <button
            type="button"
            onClick={handleLoadMore}
            className="btn btn-primary"
            disabled={buttonDisabled || loading}
          >
            {loading ? "Loading..." : "Load More Captions"}
          </button>
        </div>
      )}
    </AppLayout>
  );
}